import { Link, useNavigate } from "react-router-dom";
import { Divider, Spinner } from "@whitespace/whitespace-ui";
import { useStateMachine } from "little-state-machine";
import {
  Breadcrumb, Button, Card, Col, Row
} from "react-bootstrap";
import { updateIsUserHistoryFilterDrawerOpen } from "actions/updateIsUserHistoryFilterDrawerOpen";
import { updateSelectedUser } from "actions/updateSelectedUser";
import { useEffect, useState } from "react";
import Groups from "components/Groups/Groups";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { useAmplifyREST } from "hooks/useAmplifyREST";

const ManageUserGroups = () => {
  const { state } = useStateMachine();
  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateIsUserHistoryFilterDrawerOpen,
    updateSelectedUser
  });

  const {
    isSending: removeGroupsIsSending, errors: removeGroupsErrors, sendRequest: removeGroupsSendRequest, statusCode: removeGroupsStatusCode
  } = useAmplifyREST();

  const {
    isSending: addGroupsIsSending, errors: addGroupsErrors, sendRequest: addGroupsSendRequest, statusCode: addGroupsStatusCode
  } = useAmplifyREST();

  const [groupsSelected, setGroupsSelected] = useState<string[]>(state.selectedUser?.groups ? state.selectedUser.groups.map(group => group.groupId) : []);
  const [getGroupsIsSending, setGetGroupsIsSending] = useState(false);
  const [getGroupsErrors, setGetGroupErrors] = useState<string[]>();

  const isSending = getGroupsIsSending || removeGroupsIsSending || addGroupsIsSending;
  const errors = getGroupsErrors || removeGroupsErrors || addGroupsErrors;

  const handleGroupSelectionChange = (updatedGroupsSelected: string[]) => {
    setGroupsSelected([]);
    setGroupsSelected(updatedGroupsSelected);
  };

  const groupsUrl = `users/${encodeURIComponent(state.selectedUser!.userId)}/groups`;

  const removeGroups = () => {
    const groupsToRemove = state.selectedUser!.groups!.map(group => group.groupId).filter(userGroup => !groupsSelected.includes(userGroup));

    if (groupsToRemove.length > 0) {
      removeGroupsSendRequest(groupsUrl, "DELETE", groupsToRemove);
    } else {
      navigate("/manageuser");
    }
  };

  const addGroups = () => {
    const groupsToAdd = groupsSelected.filter(selectedGroup => !state.selectedUser!.groups!.map(group => group.groupId).includes(selectedGroup));
    if (groupsToAdd.length > 0) {
      addGroupsSendRequest(groupsUrl, "PUT", groupsToAdd);
    } else {
      removeGroups();
    }
  };

  const handleSaveClick = () => {
    addGroups();
  };

  useEffect(() => {
    if (removeGroupsStatusCode === 204) {
      navigate("/manageuser");
    }
  }, [removeGroupsStatusCode]);

  useEffect(() => {
    if (addGroupsStatusCode === 204) {
      removeGroups();
    }
  }, [addGroupsStatusCode]);

  const handleNavToUsers = () => {
    actions.updateSelectedUser(undefined);
    navigate("/users");
  };

  return (
    <>
      {errors && <AmplifyError errors={errors} />}
      {state.selectedUser
      && (
        <Card className="card-user-management">
          <Card.Header>
            <Row className="w-100">
              <Col xs={8}>
                <h1>
                  Manage User - Groups
                  <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
                </h1>
              </Col>
              <Col xs={4} className="d-flex justify-content-end">
                <span className="selected-user-title">{state.selectedUser?.username}</span>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="no-padding">
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                  <Breadcrumb.Item data-testid="breadcrumb-users" linkAs={Link} linkProps={{ to: "/" }} onClick={handleNavToUsers}>Users</Breadcrumb.Item>
                  <Breadcrumb.Item
                    data-testid="breadcrumb-users"
                    linkAs={Link}
                    linkProps={{ to: "/" }}
                    onClick={() => navigate("/manageuser")}
                  >
                    Manage User
                  </Breadcrumb.Item>
                  <Breadcrumb.Item active>Groups</Breadcrumb.Item>
                </Breadcrumb>
              </Col>
              <Col xs={4} className="d-flex justify-content-end user-filter">
                <Button
                  variant="primary"
                  className="me-2"
                  onClick={handleSaveClick}
                  disabled={isSending || groupsSelected.length === 0}
                >
                  Save
                </Button>
                <Button variant="secondary" onClick={() => navigate("/manageuser")}>
                  Back
                </Button>
              </Col>
            </Row>
            <Divider />
            <Row>
              <Col xs={12}>
                <Groups
                  groupsSelected={state.selectedUser.groups?.map(group => group.groupId)!}
                  handleGroupSelectionChange={handleGroupSelectionChange}
                  handleIsSending={setGetGroupsIsSending}
                  handleErrors={setGetGroupErrors}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default ManageUserGroups;
